import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTASection } from "@/components/CTASection";
import { InlineCTA } from "@/components/InlineCTA";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import { MapPin } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroImage from "@/assets/masonry-paver-patio-clifton-nj-2.webp";
import signsImage from "@/assets/brick-masonry-steps-totowa-nj.webp";
import materialsImage from "@/assets/paver-patio-masonry-wall-clifton-nj.webp";
import permitsImage from "@/assets/stone-work-masonry-pines-lake-nj.webp";
import outdoorKitchenImage from "@/assets/outdoor-kitchen-totowa-passaic-county-nj.webp";

const faqs = [
  {
    question: "Do I need a permit to install a paver patio in Passaic County?",
    answer:
      "Most Passaic County municipalities do require zoning approval for a new paver patio. Some towns also require a construction permit if the patio is raised or located close to the foundation walls. The requirements vary by borough and township, so it is a good idea to check with your specific town's building department before starting. We can help point you to the right office.",
  },
  {
    question:
      "How do freeze-thaw cycles in North Jersey affect paver and masonry installations?",
    answer:
      'Passaic County gets somewhere between 35 and 45 freeze-thaw cycles in a typical winter, which causes moisture in the joints and base material to expand and contract repeatedly. Over time, that movement can shift pavers, crack mortar, and push walls out of alignment if the installation was not built to handle it. Proper base depth, the right joint sand, and breathable mortar are the main ways we prevent that kind of damage.',
  },
  {
    question:
      "What type of soil do most Passaic County properties sit on?",
    answer:
      "It depends on where in the county you are. Properties up on the ridges near Ringwood and West Milford tend to sit on glacial till from the Boonton soil series, which is a mix of red and brown shale and sandstone. Down in the valleys near Paterson, Haledon, and along the Passaic River, the soil is heavier clay. Both types require specific drainage planning and base preparation to support hardscape installations.",
  },
  {
    question:
      "Can retaining walls be built on the steep lots in Woodland Park and North Haledon?",
    answer:
      "Yes, retaining walls can be built on steep lots with the right engineering. That typically includes geogrid reinforcement, which is a stabilization layer built into the wall, along with crushed stone backfill and a French drain system behind the wall to manage water pressure. Any retaining wall over four feet in height will require a construction permit and may need engineered drawings depending on the municipality.",
  },
  {
    question:
      "When is the best time of year to schedule hardscape work in Passaic County?",
    answer:
      "Spring through fall is the standard window for most paver and masonry installations in the area. Mortar needs temperatures above 40 degrees Fahrenheit to cure properly, which limits what can be done during the coldest months. If you are planning an outdoor kitchen build for summer use, it is a good idea to book early so materials and scheduling line up ahead of time.",
  },
  {
    question:
      "Are there HOA rules that affect outdoor masonry projects in Wayne or Clifton?",
    answer:
      "Some planned communities and condo associations in Wayne and Clifton do have design review boards that need to approve exterior work before it begins. That can include material choices, color selections, and sometimes the overall layout. If you live in a community with an HOA, it is worth confirming those requirements before ordering materials so nothing has to be changed after the fact.",
  },
];

const municipalities = [
  "Bloomingdale", "Clifton", "Haledon", "Hawthorne", "Little Falls",
  "North Haledon", "Passaic", "Paterson", "Pompton Lakes", "Prospect Park",
  "Ringwood", "Totowa", "Wanaque", "Wayne", "West Milford", "Woodland Park",
];

export default function PassaicCounty() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Masonry Contractor Passaic County NJ | Masonry Pro</title>
        <meta name="description" content="Masonry contractor serving all 16 Passaic County towns. Patios, retaining walls, concrete work, and steps from a crew based right here in Totowa, New Jersey." />
        <meta property="og:description" content="Masonry contractor serving all 16 Passaic County towns. Patios, retaining walls, concrete work, and steps from a crew based right here in Totowa, New Jersey." />
        <meta property="og:title" content="Masonry Contractor Passaic County NJ | Masonry Pro" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
            }))
          })}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/passaic-county.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/passaic-county.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Masonry contractor serving Passaic County, NJ" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <MapPin className="h-6 w-6" style={{ color: '#d4a853' }} />
                  <span style={{ color: '#d4a853', fontWeight: 500 }}>Passaic County, NJ</span>
                </div>
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  <Link to="/paving-services/paver-patios" className="hover:text-secondary transition-colors">Paver Patios</Link>,{" "}
                  <Link to="/masonry-services/retaining-walls" className="hover:text-secondary transition-colors">Retaining Walls</Link>, and{" "}
                  <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Masonry Installations</span> Across Passaic County, NJ
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', maxWidth: '42rem', margin: '0 auto', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Serving all 16 municipalities with expert hardscape and masonry work built for North Jersey conditions.
                </p>
              </div>
            </div>
          </section>

          {/* Intro Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto space-y-6 text-lg text-muted-foreground">
                <p>
                  <a href="https://en.wikipedia.org/wiki/Passaic_County,_New_Jersey" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">Passaic County</a><sup className="text-secondary">1</sup>{" "}
                  stretches from the lakefront communities of West Milford to the suburban neighborhoods of <Link to="/service-areas/passaic-county/clifton" className="text-secondary hover:underline">Clifton</Link> and Totowa. That range means the ground under your property can change from one block to the next. And so can the rules for building on it.
                </p>
                <p>
                  We handle{" "}
                  <Link to="/paving-services/paver-patios" className="text-secondary hover:underline">paver patio installations</Link>,{" "}
                  <Link to="/paving-services/paver-walkways" className="text-secondary hover:underline">walkways</Link>,{" "}
                  <Link to="/paving-services/paver-driveways" className="text-secondary hover:underline">driveways</Link>,{" "}
                  <Link to="/masonry-services/retaining-walls" className="text-secondary hover:underline">retaining walls</Link>,{" "}
                  <Link to="/masonry-services/fire-pits" className="text-secondary hover:underline">fireplaces</Link>,{" "}
                  <Link to="/masonry-services/outdoor-kitchens" className="text-secondary hover:underline">outdoor kitchens</Link>,{" "}
                  <Link to="/waterproofing-services/basement-waterproofing" className="text-secondary hover:underline">basement waterproofing</Link>,{" "}
                  <Link to="/waterproofing-services/patio-waterproofing" className="text-secondary hover:underline">patio sealing</Link>, and{" "}
                  <Link to="/services" className="text-secondary hover:underline">masonry work</Link>{" "}
                  for homes across all 16 Passaic County municipalities.
                </p>
                <p>
                  If you're weighing a patio project, here's a closer look at <Link to="/pro-tips/paver-patio-cost-north-jersey" className="text-secondary hover:underline">paver patio cost and what drives it</Link> in our area.
                </p>
                <p>
                  The soil here shifts depending on where you live. Glacial till sits on the ridges near Ringwood. Heavy clay fills the valleys around Paterson and Haledon. Both affect how a base needs to be built and how drainage gets handled.
                </p>
                <p>
                  Most properties in the county go through 35 to 45{" "}
                  <a href="http://climate.rutgers.edu/stateclim/" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">freeze-thaw cycles</a><sup className="text-secondary">2</sup>{" "}
                  each winter. That number changes how we prep a base, choose mortar, and plan for water movement. Getting those details right from the start is what keeps your project solid years down the road.
                </p>
                <p>
                  A lot of towns here also require zoning permits for patios, retaining walls, and driveway expansions. Each municipality handles approvals a little differently. We can walk you through what your town needs before any work begins.
                </p>
                <p>
                  If you're thinking about a project, ask about scheduling and seasonal availability for your area.
                </p>
              </div>
            </div>
          </section>

          {/* CTA 1 */}
          <InlineCTA heading="Thinking About a Project?" highlightedHeading="Check Availability" description="Call or text us directly or scroll down to request your free estimate." />

          {/* Signs Section - image left, text right */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img src={signsImage} alt="Newly constructed brick masonry steps from Masonry Pro Construction leading to a beige house in Totowa, Passaic County, NJ." className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Spotting the Signs That Your Passaic County Property Needs{" "}
                    <span className="text-gradient-gold">Hardscape Work</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Cracked walkways, sinking pavers, and retaining walls that are starting to lean are all things that show up every spring across Wayne, Hawthorne, and Totowa, once the ground finally settles. Most of the time, it's not sudden. It has been building all winter long.
                    </p>
                    <p>
                      The clay-heavy soil in the valley neighborhoods can make it worse. Water can sit longer than it should. Then the water freezes, expands, and then the thaw pulls everything apart. Catching it early can save you from a much bigger job once the damage spreads.
                    </p>
                    <p>
                      Homes built in the 1950s through the 1980s across Haledon and North Haledon tend to sit on shallower footings. That generation of construction didn't always account for the extent of ground movement here. So the steps pull away from the foundations and the walkways buckle near the edges.
                    </p>
                    <p>
                      One thing worth looking for is white staining on brick or stone surfaces. That's efflorescence. It means moisture is moving through the masonry from behind. Properties closer to the Passaic River corridor deal with this more often because of the higher humidity levels in that area.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Materials Section - text left, image right */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Choosing the Right Materials for{" "}
                    <span className="text-gradient-gold">North Jersey Freeze-Thaw Conditions</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      The material you pick for a <Link to="/paving-services/paver-patios" className="text-secondary hover:underline">patio</Link>, <Link to="/paving-services/paver-driveways" className="text-secondary hover:underline">paver driveway</Link>, <Link to="/concrete-services/driveways" className="text-secondary hover:underline">concrete driveway</Link>, or <Link to="/masonry-services/fire-pits" className="text-secondary hover:underline">fireplace</Link> surround has to hold up through 40 or more freeze-thaw cycles each winter. That rules out some options pretty quickly, and it changes how others need to be installed.
                    </p>
                    <p>
                      Bluestone is a regional favorite across North Jersey for good reason. It looks great and it handles the weather well. But on the steeper lots found throughout Woodland Park and up along the Watchung Mountain foothills, interlocking pavers can perform better because they flex with the ground instead of cracking under pressure.
                    </p>
                    <p>
                      Properties near Pompton Lakes and Wanaque Reservoir sit in higher-humidity zones. That extra moisture in the air means breathable mortar and a well-drained paver base matter more here than they would on a drier lot further inland. The wrong mortar mix can trap water behind the surface, and that is when you start seeing damage after the first hard freeze.
                    </p>
                    <p>
                      Matching the right material to your specific property conditions is something we can go over during an estimate. Every lot has its own mix of slope, soil, sun exposure, and drainage patterns that factor into the recommendation.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img src={materialsImage} alt="Paver patio with a curved masonry wall and fire pit in Clifton, Passaic County, NJ by Masonry Pro Construction" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
            </div>
          </section>

          {/* Permits Section - image left, text right */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="aspect-[3/4] rounded-xl overflow-hidden border border-border">
                  <img src={permitsImage} alt="Masonry Pro building a stone work chimney on scaffolding in Pines Lake, Wayne, Passaic County, New Jersey, 07470." className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    How Permits and Zoning Work for{" "}
                    <span className="text-gradient-gold">Patios and Retaining Walls</span> in Passaic County Towns
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Most Passaic County towns require a zoning permit before you can install a new <Link to="/paving-services/paver-patios" className="text-secondary hover:underline">patio</Link> or <Link to="/paving-services/paver-walkways" className="text-secondary hover:underline">walkway</Link>. <Link to="/masonry-services/retaining-walls" className="text-secondary hover:underline">Retaining walls</Link> over four feet typically need a{" "}
                      <a href="https://www.nj.gov/dca/codes/codreg/pdf_regs/njac_5_23_2.pdf" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">construction permit</a><sup className="text-secondary">4</sup>, and some municipalities will ask for engineered drawings before they even look at the application.
                    </p>
                    <p>
                      The tricky part is that each borough and township runs its own building department with its own setback rules and impervious coverage limits. What flies in <Link to="/service-areas/passaic-county/clifton" className="text-secondary hover:underline">Clifton</Link> may not pass in Little Falls or Bloomingdale. So a project that seems straightforward on paper can hit a wall if you haven't checked with your specific town first.
                    </p>
                    <p>
                      Some homeowners find out the hard way. They break ground, get a stop-work order, and then have to tear out finished work to meet a setback requirement they didn't know about. That is an expensive lesson and one that can be avoided with a phone call to the building department before anything starts.
                    </p>
                    <p>
                      We have worked in all 16 Passaic County municipalities and can usually point you in the right direction on what your town will need. Permit timelines vary, so it is worth getting that process started early, especially if you are trying to have the project done before summer.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA 2 */}
          <InlineCTA heading="Questions About Permits?" highlightedHeading="We Can Help" description="Give us a call or text, or fill out the form below, and we handle permits across all 16 Passaic County towns." />

          {/* Installation Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
                  What to Expect During a{" "}
                  <span className="text-gradient-gold">Patio, Walkway, or Outdoor Kitchen</span> Installation
                </h2>
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div className="space-y-6 text-lg text-muted-foreground">
                    <p>
                      Every installation starts with marking out the area and getting the grade right. That means checking the slope of your yard, figuring out where water currently moves, and planning the layout so everything drains properly once the project is finished.
                    </p>
                    <p>
                      After layout comes excavation. We dig down to the right depth based on what is going on top and what the soil conditions look like underneath. On the older lots in Paterson and Prospect Park, the narrow side yards mean we bring in smaller equipment and plan our staging carefully so the crew can work without tearing up the rest of your property.
                    </p>
                    <p>
                      The base goes in next. Crushed stone gets spread in layers and compacted a little at a time. Rushing this part is the single biggest reason patios and walkways fail within a few years. A solid base is what keeps everything level through the freeze-thaw cycles.
                    </p>
                    <p>
                      For <Link to="/masonry-services/outdoor-kitchens" className="text-secondary hover:underline">outdoor kitchens</Link>, there is additional coordination involved. Gas lines, electrical, and drainage all need to be roughed in at the right stage. We work with your other trades to keep the timeline moving and avoid having to tear anything apart later.
                    </p>
                    <p>
                      Once pavers or stone go down, the joints get filled and the surface gets a final compaction pass. For mortar-set projects, the cure time depends on the temperature. We will let you know what that looks like for your specific timeline so there are no surprises at the end.
                    </p>
                  </div>
                  <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                    <img 
                      src={outdoorKitchenImage} 
                      alt="Custom brick outdoor kitchen with built-in grill and oven in Totowa, Passaic County, NJ by Masonry Pro Construction" 
                      className="w-full h-full object-cover" 
                      loading="lazy" 
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Maintenance Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
                  Protecting Your Investment Through{" "}
                  <span className="text-gradient-gold">Passaic County's Four Seasons</span>
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p>
                    You already went through the trouble and the expense of getting the work done right. Keeping it that way takes some attention, but not as much as most people expect.
                  </p>
                  <p>
                    Salt runoff from steep driveways in North Haledon and near the Garret Mountain area can accelerate surface wear on concrete and brick over time. Choosing the right sealant and applying it at the right time of year makes a real difference. Late spring, after the last frost but before the summer heat, tends to be the best window around here.
                  </p>
                  <p>
                    Gutter and downspout management is one of those things that people overlook until there is a problem. Water pooling near patio edges or at the base of a <Link to="/masonry-services/retaining-walls" className="text-secondary hover:underline">retaining wall</Link> is a common issue, especially on the clay soils found from Haledon down through the Passaic River valley. Redirecting that water before winter can prevent shifting and drainage backups.
                  </p>
                  <p>
                    Those same clay soils along the Passaic River valley hold water against foundation walls long after a storm has passed, which is one of the more common reasons homeowners in Paterson, Haledon, and Little Falls end up looking into <Link to="/waterproofing-services/basement-waterproofing" className="text-secondary hover:underline">basement waterproofing</Link>. If you're already seeing damp spots, efflorescence on the inside of the foundation, or water pooling against the house, it's worth having that looked at before it turns into a bigger problem.
                  </p>
                  <p>
                    For paver surfaces, keeping the joint sand topped off and pulling weeds before they root deep will go a long way. Polymeric sand, which is a special jointing material that locks pavers together and blocks weed growth, can last several years before it needs to be reapplied. We can check on the condition during a seasonal visit if that would be helpful.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Service Area / Getting Here */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-8">
                  Getting to Our Service Area Across{" "}
                  <span className="text-gradient-gold">Passaic County</span>
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground text-left">
                  <p>
                    Our shop is on Highview Avenue in Totowa, which puts us right in the middle of Passaic County. I-80, Route 23, and Route 46 connect all the major towns, from Ringwood in the north to Little Falls and Totowa in the south. Most estimates across the county are same-day trips for us.
                  </p>
                  <p>
                    Some of our completed work can be found near Garret Mountain Reservation, in the neighborhoods around the Great Falls area, and along residential streets off Hamburg Turnpike in Wayne and Pompton Lakes. The Stoney Road section, down in the southwest corner of the city, sits right across the river from our shop. That is one of the areas we cover most, and you can read how we handle a <Link to="/service-areas/passaic-county/paver-patio-pennington-park-paterson" className="text-secondary hover:underline">paver patio near Pennington Park in Paterson</Link>. Closer to the center of the city, the tight lots and brick rowhomes change the job, which we cover on our page about a <Link to="/service-areas/passaic-county/paver-patio-downtown-paterson" className="text-secondary hover:underline">paver patio in downtown Paterson</Link>. If you are in Passaic County, we can get to you.
                  </p>
                </div>
                <div className="mt-8">
                  <h3 className="text-xl font-bold mb-4">All 16 Municipalities We Serve</h3>
                  <div className="flex flex-wrap justify-center gap-3">
                    {municipalities.map((town) => {
                      if (town === "Clifton") {
                        return (
                          <Link
                            key={town}
                            to="/service-areas/passaic-county/clifton"
                            className="bg-card rounded-full px-4 py-2 border border-border text-sm text-muted-foreground hover:text-secondary hover:border-secondary transition-colors"
                          >
                            {town}
                          </Link>
                        );
                      }
                      if (town === "Passaic") {
                        return (
                          <Link
                            key={town}
                            to="/service-areas/passaic-county/passaic"
                            className="bg-card rounded-full px-4 py-2 border border-border text-sm text-muted-foreground hover:text-secondary hover:border-secondary transition-colors"
                          >
                            {town}
                          </Link>
                        );
                      }
                      return (
                        <span
                          key={town}
                          className="bg-card rounded-full px-4 py-2 border border-border text-sm text-muted-foreground"
                        >
                          {town}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA 3 */}
          <CTASection serviceName="Passaic County masonry" heading="Ready to Start?" highlightedHeading="Schedule Your Estimate" />

          {/* YouTube Shorts Section */}
          <YouTubeShortsSection
            videoIds={["hMO7o0DWqAw", "qBJO5fKXbPI", "cFHz4fwoL34"]}
            title="See Our Passaic County"
            highlightedTitle="Work in Action"
          />

          {/* FAQ Section - Card Grid */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-background rounded-xl p-6 border border-border"
                  >
                    <h3 className="text-lg font-bold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Citations Accordion */}
          <section className="py-12">
            <div className="container mx-auto px-4 max-w-4xl">
              <Accordion type="single" collapsible>
                <AccordionItem value="citations" className="border-border">
                  <AccordionTrigger className="text-sm text-muted-foreground hover:no-underline">
                    Source Citations
                  </AccordionTrigger>
                  <AccordionContent>
                    <ol className="list-decimal list-inside space-y-3 text-sm text-muted-foreground">
                      <li>
                        Passaic County, New Jersey: Wikipedia.
                      </li>
                      <li>
                        Freeze-thaw cycle range (35–45 per winter): Based on NWS climate data for the Paterson, NJ observation station and local contractor field experience.
                      </li>
                      <li>
                        Boonton soil series (glacial till from red/brown shale and sandstone) as the dominant ridge soil in Passaic County: USDA Web Soil Survey / Shore Systems geotechnical reference data.
                      </li>
                      <li>
                        Retaining walls over four feet requiring a construction permit in New Jersey: NJ Uniform Construction Code (N.J.A.C. 5:23-2.1) and Cornell LII (N.J. Admin Code 19:66-7.8).
                      </li>
                    </ol>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
